import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../components/common/Button";
import ToolbarTooltipButton from "../../components/common/ToolbarTooltipButton";
import { readError } from "../../utils/apiError";
import { fromApiColors } from "../../utils/brandApi";
import { apiFetch } from "../../utils/apiFetch";
import useThemes from "../../utils/useThemes";
import { ALLOWED_EXTENSIONS, MAX_BYTES } from "./components/DocumentMedia";
import { useCreateFlow } from "../../flow/CreateFlowContext";
import { extractPhrases } from "../../utils/extractPhrases";
import useUserPlan from "../../utils/useUserPlan";
import useVideoPicker from "../../video/useVideoPicker";
import upload from "../../assets/posts/upload.svg";
import info from "../../assets/posts/info.svg";
import pdf from "../../assets/posts/pdf.svg";
import sourcePdf from "../../assets/posts/sourcePdf.svg";
import arrowRight from "../../assets/common/arrow-right.svg";
import arrowLeft from "../../assets/common/arrow-left.svg";
import paste from "../../assets/posts/paste.svg";
import draft from "../../assets/navbar/draft.svg";
import calendar from "../../assets/draft/calendar.svg";
import enFlag from "../../assets/sideBar/english.svg";
import esFlag from "../../assets/sideBar/spanish.svg";
import frFlag from "../../assets/sideBar/french.svg";
import itFlag from "../../assets/sideBar/italian.svg";

const MAX_CHARACTERS = 2000;
const OWN_TEXT_MAX_CHARACTERS = 3000;
const PDF_MAX_BYTES = 100 * 1024 * 1024;

const IMAGE_FORMATS = {
  square:      { tipo: "imagen",     formato: "imagensquare" },
  infographic: { tipo: "infografia", formato: "vertical"     },
  rectangle:   { tipo: "imagen",     formato: "horizontal"   },
};
// Maps each post idea to the API payload fields
const DEFAULT_FORMAT_BY_HOOK = {
  hook_1_disruptivo:   "square",
  hook_2_valor:        "infographic",
  hook_3_storytelling: "rectangle",
};
const IMAGE_FORMAT_OPTIONS = ["square", "infographic", "rectangle"];

const LANGUAGE_OPTIONS = [
  { code: "en", short: "EN", label: "English",  flag: enFlag },
  { code: "es", short: "ES", label: "Español",  flag: esFlag },
  { code: "fr", short: "FR", label: "Français", flag: frFlag },
  { code: "it", short: "IT", label: "Italiano", flag: itFlag },
];

export default function YourSourcesLeftColumn({
  onImagesChange,
  onGenerateSelect,
  onGenerateIdeasRequest,
  generatedHooks = null,
  generatedPosts = null,
  onGeneratePosts,
  onGenerateFromDocument,
  imageLocked,
  mobileViewStep,
  onBack,
  onConfirm,
  onImageGenerated,
  selectedPostId: selectedPostIdProp = null,
  onPostIdSelect,
  onGeneratingChange,
  generatedImagesCount = 0,
  maxGeneratedImages = 5,
  onRegenerateReady,
  editedPostContent = null,
  textConfirmed = false,
  onTextConfirm,
  onCreateCarousel,
  onReturnToMedia,
  postSegments = null,
  promptText = "",
  onPromptChange,
  postLanguage = null,
  onPostLanguageChange,
  isEditMode = false,
  contentType = null,
  onContentTypeChange,
  hasMedia = null,
  onHasMediaChange,
  onSaveAsDraft,
  onSchedulePost,
  isSavingDraft = false,
  onDocumentSelect,
  onDeleteImage,
  onDocumentDelete,
  documentFilename = null,
  postSaved = false,
  onResetText,
  onStepChange,
  onOpenAccount,
  userEmail = "",
  hasOwnText = null,
  onOwnTextModeChange,
  onProgressStage,
  ownTextDraft = "",
  onOwnTextDraftChange,
  onUseOwnText,
  videoTarget = null,
}) {
  const { t, i18n } = useTranslation();
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const carouselPdfInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const plan = useUserPlan();
  const {
    showPostIdeas, setShowPostIdeas,
    pdfFiles, setPdfFiles,
    pdfError, setPdfError,
    ideasConfirmed, setIdeasConfirmed,
    mediaConfirmed, setMediaConfirmed,
    mediaTypeConfirmed, setMediaTypeConfirmed,
    mediaSource, setMediaSource,
    imageFormat, setImageFormat,
    askImageFormat, setAskImageFormat,
    askBrandColors, setAskBrandColors,
    brandThemeId, setBrandThemeId,
    ownTextAnswer, setOwnTextAnswer,
    carouselCreated, setCarouselCreated,
    videoUpload,
  } = useCreateFlow();
  const videoPicker = useVideoPicker({
    onConfirm: ({ file, duration, poster }) => {
      // Starts in the background; the editor shows progress, errors and Cancel
      videoUpload.startUpload({
        email: userEmail,
        plan,
        target: videoTarget || "personal",
        file,
        duration,
        poster,
      });
      onTextConfirm?.(true);
      if (mobileViewStep) onStepChange?.(7);
    },
  });
  const [selectedPostId, setSelectedPostId] = useState(selectedPostIdProp);
  const [pendingBack, setPendingBack] = useState(null);   // null | "prompt" | "mediaType" | "mediaQuestion" | "question"
  const [documentError, setDocumentError] = useState(null);
  const hasAttachedMedia = imageLocked || !!documentFilename || contentType === "carousel";

  // Image generation state
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  const { themes } = useThemes(userEmail);

  useEffect(() => {
    if (generatedHooks) setShowPostIdeas(true);
  }, [generatedHooks]);

  // ================= PDF =================
  const handlePdfUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";              // allow re-picking the same file after an error
    if (!file) return;

    const looksLikePdf =
      /\.pdf$/i.test(file.name) || file.type === "application/pdf";

    if (!looksLikePdf) {
      setPdfError(t("summary.errors.format", "Only PDF files can be summarized."));
      return;
    }
    if (file.size > PDF_MAX_BYTES) {
      setPdfError(t("summary.errors.size", "The PDF exceeds the maximum allowed size of 100MB."));
      return;
    }

    setPdfError(null);
    setPdfFiles([
      { id: Math.random().toString(36).slice(2, 11), name: file.name, file },
    ]);
  };

  const handleRemove = (item) => {
    setPdfError(null);
    setPdfFiles((prev) => prev.filter((_, i) => i !== item.originalIndex));
  };

  const handlePasteFromClipboard = async () => {
    if (isEditMode) return;
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      onPromptChange?.((promptText + text).slice(0, MAX_CHARACTERS));
    } catch (err) {
      console.error("Paste failed", err);
    }
  };

  const handleInsertText = () => {
    if (pdfFiles.length) {
      onGenerateFromDocument?.(pdfFiles[0].file);
      return;
    }
    if (promptText.trim()) onGeneratePosts?.(promptText);
  };

  // ================= IMAGE =================
  const handleImageUpload = (e) => {
    if (imageLocked) { e.target.value = ""; return; }
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    onImagesChange?.(files);

    if (mobileViewStep) {
      onTextConfirm?.(true);
      onStepChange?.(7);
    }
  };

  // ================= CAROUSEL =================
  const handleCarouselPdfUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    // An uploaded PDF has no editable slides — it's a document.
    onContentTypeChange?.("document");
    onDocumentSelect?.(file);

    if (mobileViewStep) onStepChange?.(7);
  };

  // ================= EXTRACT PHRASES FROM BODY =================

  // What the current text can support. A carousel needs a body; an
  // infographic needs at least 2 short points it can lay out.
  const bodyText = postSegments?.cuerpo?.trim() || "";
  const canCreateCarousel = bodyText.length > 0;
  const canCreateInfographic =
    extractPhrases(bodyText).filter((p) => p.length > 5).length >= 2;

  // ================= LLM IMAGE GENERATION =================
  const handleGenerateImage = async (formatId = null, colors = null) => {
    const selectedPost = postIdeas.find((p) => p.id === selectedPostId);
    const isEditMode = !selectedPost && !!editedPostContent;
    if (!selectedPost && !isEditMode) {
      console.warn("No post selected, aborting image generation.");
      return;
    }
    const activePostId = selectedPostId || "hook_1_disruptivo";
    // The user's explicit choice wins. Falls back to the per-hook default so
    // regenerate and edit mode still work when no choice was ever made.
    const chosenFormat =
      formatId || imageFormat || DEFAULT_FORMAT_BY_HOOK[activePostId] || "square";
    const { tipo, formato } = IMAGE_FORMATS[chosenFormat];
    const idioma = (postLanguage || i18n.language).split("-")[0].toUpperCase();
    const isInfographic = tipo === "infografia";
    const contentToUse = editedPostContent || selectedPost.content;

    // Use postSegments.hook directly if available
    const hookLine = postSegments?.hook?.trim() ||
      contentToUse
        .split("\n")
        .map((l) => l.trim())
        .find((l) => l.length > 0) ||
      contentToUse.trim();

    // Use postSegments.cuerpo directly if available
    const mapping = {
      hook_1_disruptivo:   "opcion_1_disruptivo_corto",
      hook_2_valor:        "opcion_2_valor_medio",
      hook_3_storytelling: "opcion_3_storytelling_largo",
    };
    const postKey = mapping[activePostId];
    const cuerpo = postSegments?.cuerpo ||
      generatedPosts?.[postKey]?.cuerpo ||
      "";

    const phrases = isInfographic ? extractPhrases(cuerpo) : [];

    const payload = {
      tipo,
      formato,
      hook: hookLine,
      idioma,
      ...(isInfographic ? { phrases: phrases.filter((p) => p.length > 5) } : {}),
      ...(colors ? { colors } : {}),
    };

    setGeneratingImage(true);
    onGeneratingChange?.(true);
    setImageError(null);

    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/generate_image`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) throw new Error(await readError(response));

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      onImageGenerated?.(url, chosenFormat);
    } catch (err) {
      console.error("Image generation failed:", err.message);
      setImageError(err.message || t("errors.generateFailed"));
    } finally {
      setGeneratingImage(false);
      onGeneratingChange?.(false);
    }
  };

  // "auto" or nothing chosen means let the model pick its own palette.
  const resolveBrandColors = (themeId = brandThemeId) => {
    if (!themeId || themeId === "auto") return null;
    const theme = themes.find((th) => String(th.theme_id) === String(themeId));
    return theme ? fromApiColors(theme) : null;
  };

  useEffect(() => {
    onRegenerateReady?.((formatId, colors) =>
      handleGenerateImage(
        formatId ?? imageFormat,
        colors === undefined ? resolveBrandColors() : colors
      )
    );
  }, [selectedPostId, generatedHooks, editedPostContent, postSegments, imageFormat, brandThemeId, themes, postLanguage]);

  // ================= SELECTED POST SYNC =================
  const updateSelectedPostId = (id) => {
    setSelectedPostId(id);
    onPostIdSelect?.(id);
  };

  // ================= TOOLTIP =================
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const postIdeas = generatedHooks
    ? [
        { id: "hook_1_disruptivo", title: t("sources.styleLabel.disruptive"), content: generatedHooks.hook_1_disruptivo },
        { id: "hook_2_valor",      title: t("sources.styleLabel.value"),      content: generatedHooks.hook_2_valor      },
        { id: "hook_3_storytelling", title: t("sources.styleLabel.storytelling"), content: generatedHooks.hook_3_storytelling },
      ]
    : [];

  // Which progress stage the desktop chain is showing — same order as the chain at the bottom
  const getProgressStage = () => {
    if (isEditMode) return null;
    if (hasOwnText === null) return "start";
    if (hasOwnText && !ideasConfirmed) return "text";
    if (!hasOwnText && (!showPostIdeas || postIdeas.length === 0)) return "start";
    if (!ideasConfirmed) return "text";
    if (!mediaConfirmed) return "media";
    if (hasMedia === false) return "publish";
    if (!mediaTypeConfirmed) return "media";
    if (contentType === "document" && !documentFilename) return "media";
    if (contentType === "video" && videoUpload.status === "idle") return "media";
    if (textConfirmed) return "publish";
    return "media";
  };
  const progressStage = getProgressStage();

  useEffect(() => {
    if (!mobileViewStep) onProgressStage?.(progressStage);
  }, [progressStage, mobileViewStep]);

  const renderHeader = (customTitle) => (
    <div className="flex items-center justify-between border-b pb-2 mb-4">
      <h2 className="text-lg font-bold uppercase tracking-wide text-black-800">
        {customTitle || t("sources.header")}
      </h2>
      <div ref={wrapperRef} className="relative flex items-center">
        <button
          className="p-2"
          onClick={() => setOpen((v) => !v)}
          onMouseEnter={() => !isTouchDevice && setOpen(true)}
          onMouseLeave={() => !isTouchDevice && setOpen(false)}
        >
          <img src={info} alt="info" className="w-6 h-6" />
        </button>
        {open && (
          <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 w-[240px] md:w-[440px] bg-gray-800 text-white text-sm rounded-lg shadow-xl p-2 z-50">
            <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-800 rotate-45" />
            {t("sources.tooltipSources")}
          </div>
        )}
      </div>
    </div>
  );

  const renderPostIdeas = (onSelect, onBack, backDisabled = false) => (
    <>
      {onBack && renderBackToQuestion(onBack, backDisabled)}
      <div id="section-style-selection" className={mobileViewStep ? "space-y-3" : "space-y-3 pb-4 mb-2"}>
        <p className="text-xl font-bold text-black-800 mb-4">
          {t("sources.generateText")}
        </p>
        {postIdeas.map((post) => (
          <button
            id="card-style-option"
            key={post.id}
            type="button"
            disabled={imageLocked || textConfirmed}
            onClick={() => {
              if (imageLocked || textConfirmed) return;
              updateSelectedPostId(post.id);
              if (!mobileViewStep) onGenerateIdeasRequest?.(post.id);
              onSelect?.(post.id);      // ← added
            }}
            className={`
              flex flex-col transition-all focus:outline-none focus:ring-2
              focus:ring-primary-400 text-black-800 text-lg font-bold rounded-lg
              border-2 items-center text-left w-full p-3 px-4 my-2 gap-2
              md:text-md sm:flex-row sm:items-center sm:justify-between
              disabled:opacity-50 disabled:cursor-not-allowed
              ${
                imageLocked || textConfirmed
                  ? "opacity-50 cursor-not-allowed bg-black-0 border-black-40"
                  : selectedPostId === post.id
                    ? "bg-primary-200 border-primary-300"
                    : "bg-black-0 border-black-40 hover:border-primary-300"
              }`}
          >
            <label className="flex flex-1 items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="postStyle"
                className="mt-1 h-4 w-4 accent-black-800 border-gray-300 flex-shrink-0"
                checked={selectedPostId === post.id}
                disabled={imageLocked || textConfirmed}
                onChange={() => {
                  if (imageLocked || textConfirmed) return;
                  updateSelectedPostId(post.id);
                  if (!mobileViewStep) onGenerateIdeasRequest?.(post.id);
                }}
              />
              <div className="space-y-1 min-w-0">
                <p className="text-base lg:text-lg font-bold text-black-800 break-words">
                  {post.content}
                </p>
              </div>
            </label>
            <span className={`text-black-600 text-xs font-bold px-2 py-1 rounded-[4px] w-auto h-[26px] flex justify-center items-center flex-shrink-0 self-end sm:self-auto ${
              selectedPostId === post.id ? "bg-primary-50" : "bg-primary-500"
            }`}>
              {post.title}
            </span>
          </button>
        ))}
      </div>
    </>
  );

  // ================= OWN TEXT =================
  const OWN_TEXT_OPTIONS = [
    { id: "yes", labelKey: "sources.ownText.yes", value: true  },
    { id: "no",  labelKey: "sources.ownText.no",  value: false },
  ];

  // Back to "Do you already have the text?" — clears the editor and every later step
  const goBackToQuestion = () => {
    onResetText?.();
    onOwnTextModeChange?.(null);
    setOwnTextAnswer(null);
    setPdfFiles([]);
    setPdfError(null);
    setIdeasConfirmed(false);
    setPendingBack(null);
  };

  const renderBackToQuestion = (onClick, disabled = false) => (
    <div className="pb-4 flex">
      <Button variant="ghost" onClick={onClick} disabled={disabled} className="w-auto u-btn-sm">
        <img src={arrowLeft} alt="" /> {t("buttons.back")}
      </Button>
    </div>
  );

  const renderOwnTextQuestion = () => (
    <>
      <div id="section-own-text-question" className="space-y-3 pb-4 mb-2">
        <p className="text-xl font-bold text-black-800 mb-4">
          {t("sources.ownText.question")}
        </p>
        {OWN_TEXT_OPTIONS.map((opt) => {
          const isSelected = ownTextAnswer === opt.value;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                setOwnTextAnswer(opt.value);
                onOwnTextModeChange?.(opt.value);
              }}
              className={`flex transition-all focus:outline-none focus:ring-2
                focus:ring-primary-400 text-black-800 text-lg font-bold
                rounded-lg border-2 items-center text-left
                w-full p-3 px-4 my-2 gap-3
                ${isSelected
                  ? "bg-primary-200 border-primary-300"
                  : "bg-black-0 border-black-40 hover:border-primary-300"}`}
            >
              <input
                type="radio"
                name="hasOwnText"
                className="h-4 w-4 accent-black-800 border-gray-300 flex-shrink-0"
                checked={isSelected}
                readOnly
              />
              <span className="text-base lg:text-lg font-bold text-black-800">
                {t(opt.labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );

  // Same as the prompt box: append the clipboard text, within the limit
  const handlePasteOwnText = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      onOwnTextDraftChange?.(
        (ownTextDraft + text).slice(0, OWN_TEXT_MAX_CHARACTERS)
      );
    } catch (err) {
      console.error("Paste failed", err);
    }
  };

  const handleUseOwnTextClick = () => {
    onUseOwnText?.(ownTextDraft);
    if (mobileViewStep) return onStepChange?.(3);
    setIdeasConfirmed(true);
  };

  const renderOwnTextInput = () => (
    <div className="md:pb-4 mb-4 flex-1 flex flex-col">
      {renderBackToQuestion(() =>
        postSegments
          ? setPendingBack("question")
          : goBackToQuestion()
      )}
      <h3 className="text-xl font-bold text-black-800">{t("sources.ownText.title")}</h3>
      <p className="text-xl text-black-800 mt-1 mb-2">{t("sources.ownText.intro")}</p>
      <div className="mt-4 bg-black-0 border border-black-600 rounded-xl p-4 flex-1 flex flex-col">
        <textarea
          value={ownTextDraft}
          onChange={(e) =>
            onOwnTextDraftChange?.(
              e.target.value.slice(0, OWN_TEXT_MAX_CHARACTERS)
            )
          }
          placeholder={t("sources.ownText.placeholder")}
          className="w-full flex-1 min-h-[250px] bg-transparent resize-none
            text-lg focus:outline-none overflow-y-auto"
        />
        <div className="flex justify-end">
          <ToolbarTooltipButton
            label={t("sources.buttons.paste")}
            onClick={handlePasteOwnText}
          >
            <img src={paste} alt="" className="w-4 h-4" />
          </ToolbarTooltipButton>
        </div>
      </div>
      <div className="text-sm font-bold mt-1 mb-2">
        {ownTextDraft.length}/{OWN_TEXT_MAX_CHARACTERS}
      </div>
      <Button
        variant="primary"
        onClick={handleUseOwnTextClick}
        disabled={!ownTextDraft.trim()}
        className="w-full sm:w-[calc(50%-0.375rem)] sm:self-start my-2"
      >
        {t("sources.ownText.use")} <img src={arrowRight} alt="" className="brightness-0" />
      </Button>
    </div>
  );

  const MEDIA_OPTIONS = [
    { id: "text",  labelKey: "sources.mediaQuestion.textOnly",     value: false },
    { id: "media", labelKey: "sources.mediaQuestion.textAndMedia", value: true  },
  ];

  const renderMediaQuestion = (onSelect, onBack) => (
    <>
      {renderBackToQuestion(onBack)}      {/* ← added */}
      <div id="section-media-question" className="space-y-3 pb-4 mb-2">
        <p className="text-xl font-bold text-black-800 mb-4">
          {t("sources.mediaQuestion.title")}
        </p>
        {MEDIA_OPTIONS.map((opt) => {
          const isSelected = hasMedia === opt.value;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onHasMediaChange?.(opt.value);
                onSelect(opt.value);
              }}
              className={`flex transition-all focus:outline-none focus:ring-2 focus:ring-primary-400
                text-black-800 text-lg font-bold rounded-lg border-2 items-center text-left
                w-full p-3 px-4 my-2 gap-3
                ${isSelected ? "bg-primary-200 border-primary-300" : "bg-black-0 border-black-40 hover:border-primary-300"}`}
            >
              <input
                type="radio"
                name="hasMedia"
                className="h-4 w-4 accent-black-800 border-gray-300 flex-shrink-0"
                checked={isSelected}
                readOnly
              />
              <span className="text-base lg:text-lg font-bold text-black-800">
                {t(opt.labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );

  const renderTextSources = () => {
    const activeText = promptText;
    const hasPdf = pdfFiles.length > 0;

    return (
      <div className="md:pb-4 mb-4 flex-1 flex flex-col">
        {!isEditMode && renderBackToQuestion(goBackToQuestion)}
        <h3 className="text-xl font-bold text-black-800">{t("sources.title")}</h3>
        <p className="text-xl text-black-800 mt-1 mb-2">
          {t("sources.intro")}
        </p>

        <input
          type="file"
          ref={pdfInputRef}
          accept="application/pdf,.pdf"
          onChange={handlePdfUpload}
          className="hidden"
        />

        <div className="mt-4 flex-1 flex flex-col">
          <div
            className={`relative bg-black-0 border border-black-600 rounded-xl p-4 flex-1 flex flex-col transition-opacity ${
              hasPdf ? "opacity-50" : ""
            }`}
          >
            <textarea
              value={activeText}
              onChange={(e) => {
                if (isEditMode || hasPdf) return;
                if (e.target.value.length <= MAX_CHARACTERS) {
                  onPromptChange?.(e.target.value);
                }
              }}
              disabled={isEditMode || hasPdf}
              placeholder={t("sources.textPlaceholder")}
              className="w-full flex-1 min-h-[150px] bg-transparent resize-none text-lg focus:outline-none overflow-y-auto disabled:cursor-not-allowed"
            />
            <div className="flex justify-end">
              <ToolbarTooltipButton
                label={t("sources.buttons.paste")}
                onClick={handlePasteFromClipboard}
                disabled={isEditMode || hasPdf}
              >
                <img src={paste} alt="paste" className="w-4 h-4" />
              </ToolbarTooltipButton>
            </div>
          </div>
          <div className={`text-sm font-bold mt-1 mb-2 ${hasPdf ? "opacity-50" : ""}`}>
            {activeText.length}/{MAX_CHARACTERS}
          </div>

          <div className="flex items-center gap-3 my-3">
            <span className="h-px flex-1 bg-black-40" />
            <span className="text-sm font-bold text-black-600 uppercase">
              {t("summary.or", "or")}
            </span>
            <span className="h-px flex-1 bg-black-40" />
          </div>

          <Button
            variant="ghost"
            onClick={() => pdfInputRef.current?.click()}
            disabled={isEditMode || hasPdf || !!activeText.trim()}
            className="w-full sm:w-auto sm:self-start"
          >
            <img src={pdf} alt="" className="w-6 h-6" />
            {t("summary.upload", "Upload a PDF")}
          </Button>
          <p className="text-sm text-black-600 mt-1 mb-2">
            {t("summary.hint", "PDF only — up to 100MB")}
          </p>
        </div>

        {hasPdf && (
          <div className="mt-2">
            <p className="font-bold text-xl mb-2">{t("sources.sources")}</p>
            {pdfFiles.map((file, idx) => ({
              file, name: file.name, type: "pdf", originalIndex: idx,
            })).map((item, i) => (
              <div key={`pdf-${i}`} className="flex items-center justify-between py-2 px-4 bg-white rounded-xl border border-gray-200 mb-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <img src={sourcePdf} className="w-6 h-6 object-contain" alt="pdf" />
                  <span className="text-lg font-light text-black-800 truncate max-w-[200px] md:max-w-md">{item.name}</span>
                </div>
                <button
                  onClick={() => handleRemove(item)}
                  className="text-black-600 hover:text-red-500 transition-colors text-lg rounded-lg font-bold p-2 flex items-center justify-center gap-2 border-2 md:text-sm sm:flex-row sm:items-center focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("buttons.remove")}
                </button>
              </div>
            ))}
          </div>
        )}

        {pdfError && (
          <div className="mt-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg px-4 py-3">
            {pdfError}
          </div>
        )}

        {(() => {
          const appLanguage = i18n.language.split("-")[0];
          const activeCode = postLanguage || appLanguage;
          const active =
            LANGUAGE_OPTIONS.find((l) => l.code === activeCode) || LANGUAGE_OPTIONS[0];

          return (
            <div className="mt-2 mb-3">
              <p className="text-xl text-black-800 mt-1 mb-2">
                {t("postLanguage.willBe", { language: active.label })}
              </p>
              <div className="flex gap-2 flex-wrap mt-3">
                {LANGUAGE_OPTIONS.map((lang) => {
                  const isSelected = lang.code === active.code;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      disabled={isEditMode}
                      onClick={() =>
                        onPostLanguageChange?.(lang.code === appLanguage ? null : lang.code)
                      }
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isSelected
                          ? "bg-primary-200 border-primary-300"
                          : "bg-black-0 border-black-40 hover:border-primary-300"
                      }`}
                    >
                      <img src={lang.flag} alt="" className="w-5 h-5 object-contain" />
                      <span className="text-sm font-bold text-black-800">{lang.short}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <Button
          onClick={handleInsertText}
          disabled={
            (!activeText.trim() && !hasPdf) ||
            activeText.length > MAX_CHARACTERS
          }
          variant="primary" className="w-full sm:w-[calc(50%-0.375rem)] sm:self-start my-2"
        >
          {t("sources.buttons.insert")}
          <img src={upload} className="w-6 h-6 brightness-0" />
        </Button>
      </div>
    );
  };

  const CONTENT_TYPES = ["single", "video", "carousel", "document"];

  const renderMediaTypeCards = (onSelect, onBack) => (
    <>
      {onBack && renderBackToQuestion(onBack)}
      <div id="section-media-type" className="space-y-3 pb-4 mb-2">
        <p className="text-xl font-bold text-black-800 mb-4">
          {t("sources.contentType.title")}
        </p>
        {CONTENT_TYPES.map((type) => {
          const isSelected = false;
          return (
            <button
              key={type}
              type="button"
              onClick={() => {
                onContentTypeChange?.(type);
                onSelect?.(type);
              }}
              className={`flex transition-all focus:outline-none focus:ring-2 focus:ring-primary-400
                text-black-800 text-lg font-bold rounded-lg border-2 items-center text-left
                w-full p-3 px-4 my-2 gap-3
                ${isSelected ? "bg-primary-200 border-primary-300" : "bg-black-0 border-black-40 hover:border-primary-300"}`}
            >
              <input
                type="radio"
                name="contentType"
                className="h-4 w-4 accent-black-800 border-gray-300 flex-shrink-0"
                checked={isSelected}
                readOnly
              />
              <span className="text-base lg:text-lg font-bold text-black-800">
                {t(`sources.contentType.${type}`)}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );

  // Back to the media type cards. Nothing is deleted: the media stays and the
  // current type stays highlighted.
  const goBackToMediaType = () => {
    setMediaTypeConfirmed(false);
    setMediaSource(null);
    setPendingBack(null);
    setImageFormat(null);
    setAskImageFormat(false);
    setAskBrandColors(false);
    setBrandThemeId(null);
  };

  // Back to "text only, or text with media?". The media is kept, just not active.
  const goBackToMediaQuestion = () => {
    onTextConfirm?.(false);
    onContentTypeChange?.(null);
    onHasMediaChange?.(null);
    setMediaTypeConfirmed(false);
    setMediaSource(null);
    setMediaConfirmed(false);
    setPendingBack(null);
    setImageFormat(null);
    setAskImageFormat(false);
    setAskBrandColors(false);
    setBrandThemeId(null);
  };

  const goBackToPrompt = () => {
    onResetText?.();
    setPdfFiles([]);
    setPdfError(null);
    setShowPostIdeas(false);
    setIdeasConfirmed(false);
    setMediaConfirmed(false);
    setMediaTypeConfirmed(false);
    setMediaSource(null);
    setPendingBack(null);
    setImageFormat(null);
    setAskImageFormat(false);
    if (mobileViewStep) onStepChange?.(1);
  };

  const renderAllSet = () => {
    // Save and Schedule wait for the upload (API 4)
    const videoPending = contentType === "video" && !videoUpload.ready;
    return (
      <div id="section-all-set" className={mobileViewStep ? "" : "mt-4 mb-4"}>
        {!isEditMode && (
          <div className="pb-4 flex">
            <Button
              variant="ghost"
              onClick={() => {
                if (mobileViewStep) {
                  if (hasMedia === false) {
                    onHasMediaChange?.(null);
                    return onStepChange?.(4);
                  }
                  return onStepChange?.(7);
                }
                hasMedia === false ? goBackToMediaQuestion() : goBackToMediaType();
              }}
              className="w-auto u-btn-sm"
            >
              <img src={arrowLeft} alt="" /> {t("buttons.back")}
            </Button>
          </div>
        )}

        <p className="text-xl font-bold text-black-800">
          {t("sources.allSet.title")}
        </p>
        <p className="text-black-600 text-lg mt-1">
          {t("sources.allSet.question")}
        </p>

         <div className={`flex flex-col sm:flex-row gap-3 pt-4 ${mobileViewStep ? "" : "pb-4"}`}>
          <Button
            variant="ghost"
            onClick={onSaveAsDraft}
            disabled={isSavingDraft || postSaved || videoPending}
            className="w-full sm:flex-1"
          >
            <span>
              {isSavingDraft
                ? t("sources.allSet.saving")
                : t("sources.allSet.saveDraft")}
            </span>
            <img src={draft} alt="" className="w-6 h-6" />
          </Button>
          <Button
            variant="dark"
            onClick={onSchedulePost}
            disabled={videoPending}
            className="w-full sm:flex-1"
          >
            <span>{t("sources.allSet.schedule")}</span>
            <img src={calendar} alt="" className="w-5 h-5 brightness-0 invert" />
          </Button>
        </div>
        {videoPending && (
          <p className="text-sm text-black-400">{t("video.waitUpload")}</p>
        )}
      </div>
    );
  };

  const renderMediaSourceQuestion = (onSelect, onBack) => {
    // "Create a carousel" needs body text to build slides from
    const carouselUnavailable = contentType === "carousel" && !canCreateCarousel;

    return (
      <>
        {onBack && renderBackToQuestion(onBack)}
        <div id="section-media-source" className="space-y-3 pb-4 mb-2">
          <p className="text-xl font-bold text-black-800 mb-4">
            {contentType === "carousel"
              ? t("sources.carouselText")
              : t("sources.imageText")}
          </p>
          {["have", "need"].map((src) => {
            const isSelected = mediaSource === src;
            const isUnavailable = src === "need" && carouselUnavailable;
            return (
              <button
                key={src}
                type="button"
                disabled={isUnavailable}
                onClick={() => {
                  setMediaSource(src);
                  onSelect?.(src);
                }}
                className={`flex transition-all focus:outline-none focus:ring-2
                  focus:ring-primary-400 text-black-800 text-lg font-bold
                  rounded-lg border-2 items-center text-left
                  w-full p-3 px-4 my-2 gap-3
                  ${isUnavailable
                    ? "bg-black-0 border-black-40 opacity-50 cursor-not-allowed"
                    : isSelected
                      ? "bg-primary-200 border-primary-300"
                      : "bg-black-0 border-black-40 hover:border-primary-300"}`}
              >
                <input
                  type="radio"
                  name="mediaSource"
                  className="h-4 w-4 accent-black-800 border-gray-300 flex-shrink-0"
                  checked={isSelected}
                  disabled={isUnavailable}
                  readOnly
                />
                <span className="text-base lg:text-lg font-bold text-black-800">
                  {src === "have"
                    ? t(contentType === "carousel"
                        ? "sources.options.uploadCarousel"
                        : "sources.options.upload")
                    : t(contentType === "carousel"
                        ? "sources.options.generateCarousel"
                        : "sources.options.generate")}
                </span>
              </button>
            );
          })}
          {carouselUnavailable && (
            <div className="space-y-2 mb-4">
              <span className="text-sm sm:text-lg bg-secondary-100 px-2 py-1 inline-block">
                {t("sources.unavailable.carousel")}
              </span>
            </div>
          )}
        </div>
      </>
    );
  };

   const renderImageFormatQuestion = (onSelect, onBack) => (
    <>
      {onBack && renderBackToQuestion(onBack)}
      <div id="section-image-format" className="space-y-3 pb-4 mb-2">
        <p className="text-xl font-bold text-black-800 mb-4">
          {t("sources.imageFormat.title")}
        </p>
        {IMAGE_FORMAT_OPTIONS.map((id) => {
          const isSelected = imageFormat === id;
          // An infographic needs a few points from the body to lay out
          const isUnavailable = id === "infographic" && !canCreateInfographic;
          return (
            <button
              key={id}
              type="button"
              disabled={isUnavailable}
              onClick={() => {
                setImageFormat(id);
                onSelect?.(id);
              }}
              className={`flex transition-all focus:outline-none focus:ring-2
                focus:ring-primary-400 text-black-800 text-lg font-bold
                rounded-lg border-2 items-center text-left
                w-full p-3 px-4 my-2 gap-3
                ${isUnavailable
                  ? "bg-black-0 border-black-40 opacity-50 cursor-not-allowed"
                  : isSelected
                    ? "bg-primary-200 border-primary-300"
                    : "bg-black-0 border-black-40 hover:border-primary-300"}`}
            >
              <input
                type="radio"
                name="imageFormat"
                className="h-4 w-4 accent-black-800 border-gray-300 flex-shrink-0"
                checked={isSelected}
                disabled={isUnavailable}
                readOnly
              />
              <span className="text-base lg:text-lg font-bold text-black-800">
                {t(`sources.imageFormat.${id}`)}
              </span>
            </button>
          );
        })}
        {!canCreateInfographic && (
          <div className="space-y-2 mb-4">
            <span className="text-sm sm:text-lg bg-secondary-100 px-2 py-1 inline-block">
              {t("sources.unavailable.infographic")}
            </span>
          </div>
        )}
      </div>
    </>
  );

  const BRAND_COLOR_OPTIONS = [
    { id: "brand", labelKey: "sources.brandColors.useBrand" },
    { id: "auto",  labelKey: "sources.brandColors.useAuto"  },
  ];

  const renderBrandColorsQuestion = (onSelect, onBack) => (
    <>
      {onBack && renderBackToQuestion(onBack)}      {/* ← added */}
      <div id="section-brand-colors" className="space-y-3 pb-4 mb-2">
        <p className="text-xl font-bold text-black-800 mb-4">
          {t("sources.brandColors.title")}
        </p>

        {themes.map((theme) => {
          const isSelected = String(brandThemeId) === String(theme.theme_id);
          return (
            <button
              key={theme.theme_id}
              type="button"
              onClick={() => {
                setBrandThemeId(theme.theme_id);
                onSelect?.(theme.theme_id);
              }}
              className={`flex transition-all focus:outline-none focus:ring-2 focus:ring-primary-400
                text-black-800 text-lg font-bold rounded-lg border-2 items-center text-left
                w-full p-3 px-4 my-2 gap-3
                ${isSelected ? "bg-primary-200 border-primary-300" : "bg-black-0 border-black-40 hover:border-primary-300"}`}
            >
              <input
                type="radio"
                name="brandTheme"
                className="h-4 w-4 accent-black-800 border-gray-300 flex-shrink-0"
                checked={isSelected}
                readOnly
              />
              <span className="text-base lg:text-lg font-bold text-black-800 flex-1">
                {theme.name}
              </span>
              <span className="flex gap-1 flex-shrink-0">
                {[theme.background, theme.text, theme.highlight].map((c, i) => (
                  <span
                    key={i}
                    className="w-3 h-3 rounded-full border border-black-40"
                    style={{ background: c }}
                  />
                ))}
              </span>
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => {
            setBrandThemeId("auto");
            onSelect?.("auto");
          }}
          className={`flex transition-all focus:outline-none focus:ring-2 focus:ring-primary-400
            text-black-800 text-lg font-bold rounded-lg border-2 items-center text-left
            w-full p-3 px-4 my-2 gap-3
            ${brandThemeId === "auto" ? "bg-primary-200 border-primary-300" : "bg-black-0 border-black-40 hover:border-primary-300"}`}
        >
          <input
            type="radio"
            name="brandTheme"
            className="h-4 w-4 accent-black-800 border-gray-300 flex-shrink-0"
            checked={brandThemeId === "auto"}
            readOnly
          />
          <span className="text-base lg:text-lg font-bold text-black-800">
            {t("sources.brandColors.useAuto")}
          </span>
        </button>

        <button
          type="button"
          onClick={() => onOpenAccount?.()}
          className="text-base text-black-800 font-bold underline hover:text-black mb-2 ml-1"
        >
          {t("sources.brandColors.setupColors")} →
        </button>
      </div>
    </>
  );

  const cancelImageFormatQuestion = () => {
    setAskImageFormat(false);
    setImageFormat(null);
    setAskBrandColors(false);
    setBrandThemeId(null);
    setMediaSource(null);
  };

  // Back from "have it / generate it" to the media type cards
  const goBackToMediaTypeCards = () => {
    setMediaTypeConfirmed(false);
    setMediaSource(null);
    setDocumentError(null);
    onTextConfirm?.(false);
    onContentTypeChange?.(null);
  };

  // Does this media type already have something the user made?
  const hasMediaOfType = (type) =>
    type === "single"
      ? imageLocked || generatedImagesCount > 0
      : type === "video"
        ? videoUpload.status !== "idle" || !!videoUpload.saved
        : type === "document"
          ? !!documentFilename
          : carouselCreated;

  // Clicking a media type card. If that type already has media, skip every
  // question and go straight back to it. Returns whether it did.
  const pickMediaType = (type) => {
     const exists = hasMediaOfType(type);
    onTextConfirm?.(exists);
    if (exists) onReturnToMedia?.(type);
    setMediaTypeConfirmed(true);
    setMediaSource(null);
    setImageFormat(null);
    setAskImageFormat(false);
    setAskBrandColors(false);
    setBrandThemeId(null);
    return exists;
  };

  // Back from the brand colours question to the image format question
  const goBackFromBrandColors = () => {
    setAskBrandColors(false);
    setBrandThemeId(null);
    setImageFormat(null);
  };

  // Clicking "I have it" or "Generate" — same logic Continue used to run
  const startFromMediaSource = (src) => {
    if (contentType === "carousel") {
      if (src === "have") {
        carouselPdfInputRef.current?.click();   // uploaded PDF → becomes a document
      } else {
        onCreateCarousel?.();
        onTextConfirm?.(true);                  // generate slides
        setCarouselCreated(true);
        if (mobileViewStep) onStepChange?.(7);
      }
    } else if (src === "have") {
      imageInputRef.current?.click();
      onTextConfirm?.(true);
    } else {
      setAskImageFormat(true);
    }
  };

  // Clicking a brand colour option — generates the image
  const generateWithBrandColors = (themeId) => {
    const colors = resolveBrandColors(themeId);
    onGenerateSelect?.();
    handleGenerateImage(imageFormat, colors);
    onTextConfirm?.(true);
    setAskImageFormat(false);
    setAskBrandColors(false);
    if (mobileViewStep) onStepChange?.(7);
  };

  // ================= DOCUMENT UPLOAD =================
  const handleDocumentFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file after an error
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setDocumentError(t("document.errors.format"));
      return;
    }
    if (file.size > MAX_BYTES) {
      setDocumentError(t("document.errors.size"));
      return;
    }
    setDocumentError(null);
    onDocumentSelect?.(file);
    if (mobileViewStep) onStepChange?.(7);
  };

  const renderDocumentUpload = (onBack) => (
    <>
      {onBack && renderBackToQuestion(onBack)}
      <div id="section-document-upload" className="pb-4 mb-2">
        <input
          type="file"
          ref={documentInputRef}
          accept=".pdf,.ppt,.pptx,.doc,.docx"
          onChange={handleDocumentFile}
          className="hidden"
        />
        <h3 className="text-xl font-bold text-black-800">{t("document.title")}</h3>
        <p className="text-xl text-black-800 mt-1 mb-4">{t("document.description")}</p>
        <Button
          variant="dark"
          onClick={() => documentInputRef.current?.click()}
          className="w-full"
        >
          {t("document.upload")}
        </Button>
        <p className="text-sm text-black-400 mt-2">{t("document.hint")}</p>
        {documentError && (
          <div className="space-y-2 mt-3">
            <span className="text-sm sm:text-lg bg-secondary-100 px-2 py-1 inline-block">
              {documentError}
            </span>
          </div>
        )}
      </div>
    </>
  );

  // ================= VIDEO UPLOAD =================
  const renderVideoPosterModal = () => videoPicker.modal;

  const renderVideoUpload = (onBack) => (
    <>
      {onBack && renderBackToQuestion(onBack)}
      <div id="section-video-upload" className="pb-4 mb-2">
        <input
          type="file"
          ref={videoPicker.inputRef}
          accept="video/mp4,.mp4"
          onChange={videoPicker.handleFile}
          className="hidden"
        />
        <h3 className="text-xl font-bold text-black-800">{t("video.title")}</h3>
        <p className="text-xl text-black-800 mt-1 mb-4">{t("video.description")}</p>
        <Button variant="dark" onClick={videoPicker.openPicker} className="w-full">
          {t("video.upload")}
        </Button>
        <p className="text-sm text-black-400 mt-2">{t("video.hint")}</p>
        {videoPicker.error && (
          <div className="space-y-2 mt-3">
            <span className="text-sm sm:text-lg bg-secondary-100 px-2 py-1 inline-block">
              {videoPicker.error}
            </span>
          </div>
        )}
      </div>
    </>
  );

  const renderPendingBackDialog = () =>
    pendingBack && (
      <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-4 w-full max-w-[400px] shadow-xl">
          <h3 className="text-lg font-bold mb-4">
            {pendingBack === "prompt" || pendingBack === "question"
              ? t("dialog.changeText.title")
              : t("dialog.changeMedia.title")}
          </h3>
          <p className="bg-link-100 text-black-800 text-lg px-2 mb-4">
            {pendingBack === "question"
              ? t("sources.ownText.discard")
              : pendingBack === "prompt"
                ? t("dialog.changeText.message")
                : t("dialog.changeMedia.message")}
          </p>
          <div className="flex flex-col sm:flex-row justify-end gap-3 text-sm sm:text-lg font-bold">
            <Button variant="ghost" onClick={() => setPendingBack(null)} className="w-full sm:w-auto">
              {t("buttons.cancel")}
            </Button>
            <Button
              variant="dark"
              onClick={() => {
                if (pendingBack === "question") return goBackToQuestion();
                if (pendingBack === "prompt") return goBackToPrompt();
                if (pendingBack === "mediaQuestion") return goBackToMediaQuestion();
                goBackToMediaType();
              }}
              className="w-full sm:w-auto"
            >
              {t("buttons.continue")}
            </Button>
          </div>
        </div>
      </div>
    );

  const mobileShell = (body, actions) => (
    <aside className="bg-black-10 p-4 flex-1 flex flex-col">
      {renderHeader()}
      <input
        type="file"
        ref={imageInputRef}
        multiple
        accept="image/png,image/jpeg"
        onChange={handleImageUpload}
        className="hidden"
      />
      <input
        type="file"
        ref={carouselPdfInputRef}
        accept=".pdf"
        onChange={handleCarouselPdfUpload}
        className="hidden"
      />
      <div className="flex-1">{body}</div>
      {actions && <div className="mt-auto space-y-3">{actions}</div>}
      {renderPendingBackDialog()}
      {renderVideoPosterModal()}
    </aside>
  );

  /* ===== MOBILE — STEP 1 · Prompt & media question ===== */
  if (mobileViewStep === 1) {
    return (
      <aside className="bg-black-10 p-4 flex-1 flex flex-col">
        {renderHeader()}
        {hasOwnText === null
          ? renderOwnTextQuestion()
          : hasOwnText
            ? renderOwnTextInput()
            : showPostIdeas && postIdeas.length > 0 ? renderPostIdeas() : renderTextSources()}
        {renderPendingBackDialog()}
      </aside>
    );
  }

  /* ===== MOBILE — STEP 2 · Choose a post idea ===== */
  if (mobileViewStep === 2) {
    return mobileShell(
      renderPostIdeas(
        (postId) => {
          onGenerateIdeasRequest?.(postId);
          onConfirm?.();
        },
        () => setPendingBack("prompt")
      )
    );
  }

  /* ===== MOBILE — STEP 4 · Text only, or text + media ===== */
  if (mobileViewStep === 4) {
    return mobileShell(
      renderMediaQuestion(
        (hasMediaValue) => onStepChange?.(hasMediaValue === false ? 8 : 5),
        () => onStepChange?.(3)
      )
    );
  }

  /* ===== MOBILE — STEP 5 · Which media type ===== */
  if (mobileViewStep === 5) {
    return mobileShell(
      renderMediaTypeCards(
        (type) => {
          const exists = pickMediaType(type);
          onStepChange?.(exists ? 7 : type === "document" || type === "video" ? 9 : 6);
        },
        () => {
          onHasMediaChange?.(null);
          onContentTypeChange?.(null);
          onStepChange?.(4);
        }
      )
    );
  }

  /* ===== MOBILE — STEP 6 · Have it, or generate it ===== */
  if (mobileViewStep === 6) {
    return mobileShell(
      askBrandColors
        ? renderBrandColorsQuestion(generateWithBrandColors, goBackFromBrandColors)
        : askImageFormat
          ? renderImageFormatQuestion(() => setAskBrandColors(true), cancelImageFormatQuestion)
          : renderMediaSourceQuestion(startFromMediaSource, () => {
              goBackToMediaTypeCards();
              onStepChange?.(5);
            })
    );
  }

  /* ===== MOBILE — STEP 9 · Upload the document or the video ===== */
  if (mobileViewStep === 9) {
    const goBack = () => {
      goBackToMediaTypeCards();
      onStepChange?.(5);
    };
    return mobileShell(
      contentType === "video" ? renderVideoUpload(goBack) : renderDocumentUpload(goBack)
    );
  }

  /* ===== MOBILE — STEP 8 · All set ===== */
  if (mobileViewStep === 8) {
    return mobileShell(renderAllSet(), null);
  }

  /* ===== DESKTOP — editing an existing post ===== */
  if (!mobileViewStep && isEditMode) {
    return (
      <aside className="bg-black-20 rounded-tl-2xl rounded-tr-2xl md:rounded-tr-none rounded-bl-2xl md:rounded-bl-2x p-4 shadow-sm">
        {renderHeader()}
        {renderAllSet()}
      </aside>
    );
  }

  /* ===== DESKTOP — left column ===== */
  return (
    <aside className="bg-black-20 rounded-tl-2xl rounded-tr-2xl md:rounded-tr-none rounded-bl-2xl md:rounded-bl-2x p-4 shadow-sm">
      {renderHeader()}
      <input
        type="file"
        ref={imageInputRef}
        multiple
        accept="image/png,image/jpeg"
        onChange={handleImageUpload}
        className="hidden"
      />
      <input 
        type="file" 
        ref={carouselPdfInputRef} 
        accept=".pdf" 
        onChange={handleCarouselPdfUpload} 
        className="hidden" 
      />

      {hasOwnText === null ? (
        /* 0 · Do you already have the text? */
        renderOwnTextQuestion()

      ) : hasOwnText && !ideasConfirmed ? (
        /* 1 · Own text — paste or write it */
        renderOwnTextInput()

      ) : !hasOwnText && (!showPostIdeas || postIdeas.length === 0) ? (
        /* 1 · Prompt */
        renderTextSources()

      ) : !ideasConfirmed ? (
        /* 2 · Pick a post idea */
        <>
          {renderPostIdeas(undefined, () => setPendingBack("prompt"), textConfirmed)}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="dark"
              disabled={!selectedPostId}
              onClick={() => setIdeasConfirmed(true)}
              className="w-full sm:w-[calc(50%-0.375rem)]"
            >
              {t("sources.confirmStyle")} <img src={arrowRight} alt="" />
            </Button>
          </div>
        </>

      ) : !mediaConfirmed ? (
        /* 3 · Text only, or text + media? */
        renderMediaQuestion(
          () => setMediaConfirmed(true),
          () => setIdeasConfirmed(false)
        )

      ) : hasMedia === false ? (
        /* 4a · Text only — schedule or save */
        renderAllSet()

      ) : !mediaTypeConfirmed ? (
        /* 4b · Which media type? */
        renderMediaTypeCards(
          (type) => pickMediaType(type),
          goBackToMediaQuestion
        )

      ) : contentType === "video" && videoUpload.status === "idle" ? (
        /* 4c · Upload the video */
        renderVideoUpload(goBackToMediaTypeCards)

      ) : contentType === "document" && !documentFilename && !isEditMode ? (
        /* 4c · Upload the document */
        renderDocumentUpload(goBackToMediaTypeCards)

      ) : textConfirmed ? (
        /* 5 · Media is in the editor — schedule or save */
        renderAllSet()

      ) : askImageFormat ? (
        /* 5b · Image format — then brand colors */
        askBrandColors
          ? renderBrandColorsQuestion(generateWithBrandColors, goBackFromBrandColors)
          : renderImageFormatQuestion(() => setAskBrandColors(true), cancelImageFormatQuestion)

      ) : (
        /* 5 · Image or carousel — have it, or generate it? */
        renderMediaSourceQuestion(startFromMediaSource, goBackToMediaTypeCards)
      )}
      {renderPendingBackDialog()}
      {renderVideoPosterModal()}
    </aside>
  );
}